import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, LocateFixed } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  describeLineDrift,
  type NarrationDriftReport,
  type NarrationLineDrift,
} from "./narrationDrift";
import type { NarrationLine, NarrationTimeline } from "./narrationTimeline";

// ---------------------------------------------------------------------------
// TranscriptPanel — the narration read as text, in sync with the video
// (DIR-005).
//
// Clicking a line seeks to it, and the line under the playhead highlights as it
// plays. Auto-scroll follows the playhead but yields the moment an operator
// scrolls by hand — nothing is more annoying than a transcript that yanks itself
// away while you are reading it — and offers an explicit way back.
// ---------------------------------------------------------------------------

/** How long a manual scroll suppresses auto-follow. */
const FOLLOW_SUPPRESSION_MS = 3000;
/** Above this many lines the list gets its own scroll box. */
const SCROLL_AFTER_LINES = 12;

function formatClock(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = Math.floor(safe % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

function LineBadges({
  line,
  drift,
  isAdmin,
}: {
  line: NarrationLine;
  drift: NarrationLineDrift | undefined;
  isAdmin?: boolean;
}) {
  const description = drift ? describeLineDrift(drift) : null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {drift?.attributedSceneId != null && (
        <Badge variant="outline" className="text-[10px]">
          scene {String(drift.attributedSceneId)}
        </Badge>
      )}
      {drift?.orphaned && (
        <Badge variant="destructive" className="text-[10px]">
          orphaned
        </Badge>
      )}
      {!drift?.orphaned && drift?.onScreenMismatch && (
        <Badge variant="outline" className="border-amber-500 text-[10px]">
          over scene {String(drift.onScreenSceneId ?? "?")}
        </Badge>
      )}
      {description && !drift?.orphaned && (
        <Badge
          variant={drift?.severity === "critical" ? "destructive" : "outline"}
          className={cn(
            "text-[10px]",
            drift?.severity === "warn" && "border-amber-500",
          )}
        >
          {description}
        </Badge>
      )}
      {line.exceedsVideoDuration && (
        <Badge variant="destructive" className="text-[10px]">
          cut off by the video end
        </Badge>
      )}
      {isAdmin &&
        line.qcCodes.map((code) => (
          <Badge key={code} variant="outline" className="text-[10px]">
            {code}
          </Badge>
        ))}
    </div>
  );
}

export function TranscriptPanel({
  narration,
  drift,
  activeLineIndex,
  onSeekToLine,
  onSelectScene,
  isAdmin,
  tone = "surface",
}: {
  narration: NarrationTimeline;
  drift: NarrationDriftReport;
  /** Index of the line under the playhead, or -1 between lines. */
  activeLineIndex: number;
  onSeekToLine: (line: NarrationLine) => void;
  onSelectScene?: (sceneIndex: number) => void;
  isAdmin?: boolean;
  /** "dark" adapts the muted tones for the black fullscreen surface. */
  tone?: "surface" | "dark";
}) {
  const isDark = tone === "dark";
  const listRef = useRef<HTMLOListElement | null>(null);
  const activeRef = useRef<HTMLLIElement | null>(null);
  const [following, setFollowing] = useState(true);
  const suppressUntilRef = useRef(0);

  const driftByLine = new Map(drift.lines.map((entry) => [entry.index, entry]));

  // A manual scroll means "I am reading" — stop chasing the playhead for a bit.
  const suppressFollow = useCallback(() => {
    suppressUntilRef.current = Date.now() + FOLLOW_SUPPRESSION_MS;
    setFollowing(false);
  }, []);

  useEffect(() => {
    if (!following) {
      const timer = window.setTimeout(() => {
        if (Date.now() >= suppressUntilRef.current) setFollowing(true);
      }, FOLLOW_SUPPRESSION_MS);
      return () => window.clearTimeout(timer);
    }
    if (activeLineIndex < 0) return;
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeLineIndex, following]);

  const copyTranscript = useCallback(() => {
    const text = narration.lines
      .map((line) => `${formatClock(line.startSeconds)}  ${line.text}`)
      .join("\n");
    void navigator.clipboard?.writeText(text);
  }, [narration.lines]);

  const scrolls = narration.lines.length > SCROLL_AFTER_LINES;

  return (
    <div className="space-y-2" data-testid="director-view-transcript">
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-xs",
            isDark ? "text-white/70" : "text-muted-foreground",
          )}
        >
          {narration.lines.length} narration{" "}
          {narration.lines.length === 1 ? "line" : "lines"}
          {narration.droppedLineCount > 0 &&
            ` · ${narration.droppedLineCount} never spoken`}
        </span>
        <div className="flex items-center gap-1">
          {!following && (
            <Button
              variant="ghost"
              size="sm"
              className={cn("h-7 text-xs", isDark && "text-white")}
              onClick={() => {
                suppressUntilRef.current = 0;
                setFollowing(true);
              }}
            >
              <LocateFixed className="mr-1 h-3 w-3" />
              Follow playback
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 text-xs", isDark && "text-white")}
            onClick={copyTranscript}
          >
            <Copy className="mr-1 h-3 w-3" />
            Copy
          </Button>
        </div>
      </div>

      <ol
        ref={listRef}
        onWheel={suppressFollow}
        onTouchMove={suppressFollow}
        className={cn("space-y-1", scrolls && "max-h-72 overflow-y-auto pr-1")}
      >
        {narration.lines.map((line) => {
          const isActive = line.index === activeLineIndex;
          const lineDrift = driftByLine.get(line.index);
          return (
            <li
              key={line.index}
              ref={isActive ? activeRef : undefined}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "rounded px-2 py-1.5 transition-colors",
                isActive
                  ? isDark
                    ? "bg-white/15"
                    : "bg-primary/10"
                  : isDark
                    ? "hover:bg-white/10"
                    : "hover:bg-muted/60",
              )}
            >
              <button
                type="button"
                onClick={() => onSeekToLine(line)}
                className="flex w-full items-start gap-2 text-left"
                data-testid={`director-view-transcript-line-${line.index}`}
              >
                <span
                  className={cn(
                    "mt-0.5 shrink-0 text-[11px] tabular-nums",
                    isDark ? "text-white/60" : "text-muted-foreground",
                  )}
                >
                  {formatClock(line.startSeconds)}
                </span>
                <span
                  dir="auto"
                  className={cn(
                    "text-sm",
                    isActive
                      ? isDark
                        ? "text-white"
                        : "text-foreground"
                      : isDark
                        ? "text-white/70"
                        : "text-muted-foreground",
                  )}
                >
                  {line.words && line.words.length > 0
                    ? line.words.map((word, position) => (
                        <span key={`${line.index}-${position}`}>
                          {position > 0 ? " " : ""}
                          {word.text}
                        </span>
                      ))
                    : line.text}
                </span>
              </button>
              <div
                className="mt-1 pl-[3.25rem]"
                onClick={() => {
                  if (lineDrift?.attributedSceneIndex != null) {
                    onSelectScene?.(lineDrift.attributedSceneIndex);
                  }
                }}
                role="presentation"
              >
                <LineBadges line={line} drift={lineDrift} isAdmin={isAdmin} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
